import { cn } from "@/lib/utils";
import { useCommunityPanelStore } from "@/store/zustand/communityPanelStore";
import React, { useState, useEffect } from "react";
import { LayoutOption, CollectionType } from "@/utils/types";
import { Blocks, Grid2X2, LayoutPanelTop, LayoutPanelLeft } from "lucide-react";
import { useDispatch } from "react-redux";
import { setDraggingComponent } from "@/store/iframeSlice";

interface BaseCardProps {
  title?: string;
  count?: number;
  imageUrl?: string;
  selected?: boolean;
  onClick?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  className?: string;
  isThumbnail?: boolean;
}
export const BaseCard: React.FC<BaseCardProps> = ({
  title,
  count,
  imageUrl,
  selected,
  onClick,
  onDragStart,
  className,
  isThumbnail,
}) => {
  const [PlaceholderIcon, setPlaceholderIcon] = useState<React.ComponentType<
    React.SVGProps<SVGSVGElement>
  > | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    const placeholderIcons: React.ComponentType<
      React.SVGProps<SVGSVGElement>
    >[] = [Blocks, Grid2X2, LayoutPanelTop, LayoutPanelLeft];
    setPlaceholderIcon(
      () =>
        placeholderIcons[Math.floor(Math.random() * placeholderIcons.length)]
    );
  }, []);

  return (
    <div
      className={cn(
        "p-2 rounded-lg cursor-pointer transition-all h-min border w-full ",
        isThumbnail
          ? "bg-secondary-n-950 hover:bg-secondary-n-700"
          : "bg-background-primary hover:border-primary-0",
        className,
        selected ? "border-primary-0" : "border-border-primary"
      )}
      onClick={onClick}
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragEnd={() => {
        dispatch(setDraggingComponent(null));
      }}
    >
      <div
        className="w-full min-h-[40px] h-full rounded-lg flex items-center justify-center"
        style={{
          background: "linear-gradient(90deg, #1F1F22 0%, #2F3035 100%)",
        }}
      >
        {isThumbnail ? (
          <div className="h-full w-full bg-background-primary p-2 flex items-center justify-center overflow-hidden rounded-sm">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="object-scale-down rounded-sm max-w-full max-h-full"
                draggable={false}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center rounded-sm">
                {PlaceholderIcon && (
                  <PlaceholderIcon className="w-8 h-8 text-gray-400" />
                )}
              </div>
            )}
          </div>
        ) : imageUrl ? (
          <div className="rounded-sm overflow-hidden w-full h-full">
            <img
              src={imageUrl}
              alt={title}
              className="w-min-content object-scale-down"
              draggable={false}
            />
          </div>
        ) : (
          <div className="w-full min-h-[73px] flex items-center justify-center rounded-sm">
            {PlaceholderIcon && (
              <PlaceholderIcon className="w-10 h-10 text-gray-400" />
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between mt-2 items-center select-none">
        <span className="text-2xs font-medium text-secondary-n-50 truncate">
          {title}
        </span>
        {count !== undefined && (
          <span className="text-xs text-primary rounded-xs bg-secondary-300 px-2 py-1">
            {count}
          </span>
        )}
      </div>
    </div>
  );
};
export const LayoutCard: React.FC<{
  layout: LayoutOption;
  selected?: boolean;
  onClick: () => void;
  count?: number;
}> = ({ layout, selected, onClick, count = 0 }) => {
  return (
    <BaseCard
      title={layout.name}
      count={count}
      imageUrl={layout.icon}
      selected={selected}
      onClick={onClick}
    />
  );
};
export const CollectionCard: React.FC<{
  collection: CollectionType;
  count: number;
  onSelect: () => void;
}> = ({ collection, count, onSelect }) => {
  const { selectedCollections } = useCommunityPanelStore();
  const isSelected = selectedCollections.includes(collection.id.toString());

  return (
    <BaseCard
      title={collection.name}
      count={count}
      imageUrl={collection.image_url || undefined}
      selected={isSelected}
      onClick={onSelect}
    />
  );
};
